import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiTrash2 } from 'react-icons/fi';
import { deleteAccountSubmission, getAccountSubmissions } from '../api/accountSubmissionsApi';
import ConfirmDialog from '../components/dashboard/ConfirmDialog';

const ACCOUNT_KIND_OPTIONS = [
  { value: '', label: 'All account types' },
  { value: 'cfd', label: 'CFD' },
  { value: 'investment', label: 'Investment' },
  { value: 'saving', label: 'Saving' },
];

const AMOUNT_OPTIONS = [
  { value: '', label: 'All amounts' },
  { value: 'Below 5k', label: 'Below 5k' },
  { value: '5k – 25k', label: '5k – 25k' },
  { value: '25k – 100k', label: '25k – 100k' },
  { value: 'Above 100k', label: 'Above 100k' },
];

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
};

const AccountSubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountKind, setAccountKind] = useState('');
  const [country, setCountry] = useState('');
  const [amount, setAmount] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadSubmissions = async () => {
    setIsLoading(true);
    setError('');

    try {
      const data = await getAccountSubmissions();
      setSubmissions(data);
    } catch (err) {
      setSubmissions([]);
      setError(err.message || 'Failed to load submissions.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSubmissions();
  }, []);

  const countryOptions = useMemo(() => {
    const uniqueCountries = [...new Set(submissions.map((item) => item.country).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b)
    );

    return [
      { value: '', label: 'All countries' },
      ...uniqueCountries.map((value) => ({ value, label: value })),
    ];
  }, [submissions]);

  const filteredSubmissions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return submissions.filter((submission) => {
      if (accountKind && submission.accountKind !== accountKind) {
        return false;
      }

      if (country && submission.country !== country) {
        return false;
      }

      if (amount && submission.amount !== amount) {
        return false;
      }

      if (query) {
        const haystack = `${submission.fullName} ${submission.email}`.toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }

      if (dateFrom) {
        const from = new Date(`${dateFrom}T00:00:00`);
        if (new Date(submission.createdAt) < from) {
          return false;
        }
      }

      if (dateTo) {
        const to = new Date(`${dateTo}T23:59:59.999`);
        if (new Date(submission.createdAt) > to) {
          return false;
        }
      }

      return true;
    });
  }, [submissions, searchQuery, accountKind, country, amount, dateFrom, dateTo]);

  const hasActiveFilters =
    searchQuery || accountKind || country || amount || dateFrom || dateTo;

  const clearFilters = () => {
    setSearchQuery('');
    setAccountKind('');
    setCountry('');
    setAmount('');
    setDateFrom('');
    setDateTo('');
  };

  const handleDeleteClick = (submission) => {
    setSubmissionToDelete(submission);
  };

  const handleDeleteCancel = () => {
    if (deletingId) {
      return;
    }

    setSubmissionToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!submissionToDelete) {
      return;
    }

    setDeletingId(submissionToDelete.id);
    setError('');

    try {
      await deleteAccountSubmission(submissionToDelete.id);
      setSubmissions((current) => current.filter((item) => item.id !== submissionToDelete.id));
      setSubmissionToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete submission.');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-[#6b635c]">Loading submissions…</p>;
  }

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 border-b border-[#e4d9c8] pb-5">
        <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#6b635c]">
          Submissions
        </p>
        <h2 className="mt-2 font-[Merriweather] text-[1.5rem] text-[#130705]">
          Create account applications
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#6b635c]">
          Review, filter, and manage applications submitted from the public create account form.
        </p>
      </header>

      {error ? (
        <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <section className="mb-5 rounded-md border border-[#e4d9c8] bg-white p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-medium text-[#130705]">Filters</p>
          {hasActiveFilters ? (
            <button
              type="button"
              onClick={clearFilters}
              className="text-[13px] font-medium text-[#785E32] hover:underline"
            >
              Clear filters
            </button>
          ) : null}
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Search</span>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Name or email"
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">
              Account type
            </span>
            <select
              value={accountKind}
              onChange={(event) => setAccountKind(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            >
              {ACCOUNT_KIND_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Country</span>
            <select
              value={country}
              onChange={(event) => setCountry(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            >
              {countryOptions.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Amount</span>
            <select
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            >
              {AMOUNT_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">From date</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">To date</span>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>
        </div>

        <p className="mt-4 text-[12px] text-[#9a9288]">
          Showing {filteredSubmissions.length} of {submissions.length} submissions
        </p>
      </section>

      {submissions.length === 0 ? (
        <p className="text-sm text-[#6b635c]">No submissions yet.</p>
      ) : filteredSubmissions.length === 0 ? (
        <p className="text-sm text-[#6b635c]">No submissions match the current filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-[#e4d9c8] bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-[#e4d9c8] bg-[#faf8f4] text-[12px] uppercase tracking-[0.12em] text-[#6b635c]">
              <tr>
                <th className="px-4 py-3 font-medium">Applicant</th>
                <th className="px-4 py-3 font-medium">Account type</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Country</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Submitted</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((submission) => (
                <tr key={submission.id} className="border-b border-[#f0e8dc] last:border-b-0">
                  <td className="px-4 py-3 font-medium text-[#130705]">{submission.fullName}</td>
                  <td className="px-4 py-3 text-[#4a4340]">{submission.accountKindLabel}</td>
                  <td className="px-4 py-3 text-[#4a4340]">{submission.email}</td>
                  <td className="px-4 py-3 text-[#4a4340]">{submission.country}</td>
                  <td className="px-4 py-3 text-[#4a4340]">{submission.amount}</td>
                  <td className="px-4 py-3 text-[#6b635c]">{formatDate(submission.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/dashboard/account-submissions/${submission.id}`}
                        className="inline-flex items-center rounded-md border border-[#e4d9c8] bg-white px-3 py-1.5 text-[12px] font-medium text-[#785E32] transition hover:bg-[#faf8f4]"
                      >
                        View
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(submission)}
                        disabled={deletingId === submission.id}
                        className="inline-flex items-center gap-1.5 rounded-md !border !border-[#e4d9c8] !bg-white px-3 py-1.5 text-[12px] font-medium !text-[#6b635c] !shadow-none transition hover:!border-[#fecdca] hover:!bg-[#fef3f2] hover:!text-[#b42318] hover:!translate-y-0 hover:!shadow-none disabled:opacity-60"
                      >
                        <FiTrash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        {deletingId === submission.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(submissionToDelete)}
        title="Delete submission?"
        message={
          submissionToDelete
            ? `Delete the application from ${submissionToDelete.fullName}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={Boolean(deletingId)}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default AccountSubmissionsPage;
